import clsx from 'clsx';
import { Filter, RotateCcw, Search, SlidersHorizontal } from 'lucide-react';
import { Input } from '@atoms/Input';
import { Button } from '@atoms/Button';
import { formatCurrency } from '@utils/formatters';
import type { OrderStatus } from '@/types';
import {
  countOrdersByStatus,
  datePresetRange,
  DEFAULT_ORDER_FILTERS,
  hasActiveOrderFilters,
  type DatePresetId,
  type OrderFilters,
  type OrderSortKey,
} from '@utils/orderFilters';
import type { Order } from '@/types';

const STATUS_TABS: Array<{ id: 'all' | OrderStatus; label: string }> = [
  { id: 'all', label: 'Tümü' },
  { id: 'pending', label: 'Beklemede' },
  { id: 'approved', label: 'Onaylandı' },
  { id: 'completed', label: 'Tamamlandı' },
  { id: 'cancelled', label: 'İptal' },
];

const DATE_PRESETS: Array<{ id: DatePresetId; label: string }> = [
  { id: 'today', label: 'Bugün' },
  { id: 'week', label: 'Son 7 Gün' },
  { id: 'month', label: 'Son 30 Gün' },
  { id: 'all', label: 'Tüm Zamanlar' },
];

interface OrderFiltersPanelProps {
  filters: OrderFilters;
  onChange: (next: OrderFilters) => void;
  orders: Order[];
  filteredCount: number;
  filteredTotal: number;
  showRestaurantSearch?: boolean;
}

function detectDatePreset(filters: OrderFilters): DatePresetId | null {
  for (const preset of DATE_PRESETS) {
    const range = datePresetRange(preset.id);
    if (filters.dateFrom === range.dateFrom && filters.dateTo === range.dateTo) {
      return preset.id;
    }
  }
  return null;
}

export function OrderFiltersPanel({
  filters,
  onChange,
  orders,
  filteredCount,
  filteredTotal,
  showRestaurantSearch = true,
}: OrderFiltersPanelProps) {
  const statusCounts = countOrdersByStatus(orders);
  const activePreset = detectDatePreset(filters);
  const filtersActive = hasActiveOrderFilters(filters);

  const patch = (partial: Partial<OrderFilters>) => onChange({ ...filters, ...partial });

  return (
    <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
            <SlidersHorizontal className="h-4 w-4" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-900">Filtreler</p>
            <p className="text-xs text-slate-500">
              {filteredCount} sipariş · {formatCurrency(filteredTotal)}
            </p>
          </div>
        </div>
        {filtersActive && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onChange(DEFAULT_ORDER_FILTERS)}
            className="text-slate-600"
          >
            <RotateCcw className="h-4 w-4" />
            Sıfırla
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => patch({ status: tab.id })}
            className={clsx(
              'inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium transition-colors',
              filters.status === tab.id
                ? 'bg-slate-900 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
            )}
          >
            {tab.label}
            <span
              className={clsx(
                'rounded-full px-1.5 py-0.5 text-[11px] font-semibold',
                filters.status === tab.id ? 'bg-white/15 text-white' : 'bg-white text-slate-500',
              )}
            >
              {statusCounts[tab.id]}
            </span>
          </button>
        ))}
      </div>

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-[2.65rem] h-4 w-4 text-slate-400" />
        <Input
          label={showRestaurantSearch ? 'Ara' : 'Sipariş Ara'}
          value={filters.search}
          onChange={(e) => patch({ search: e.target.value })}
          placeholder={
            showRestaurantSearch
              ? 'Restoran, sipariş no, ürün adı...'
              : 'Sipariş no, ürün adı...'
          }
          className="pl-9"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        {DATE_PRESETS.map((preset) => (
          <button
            key={preset.id}
            type="button"
            onClick={() => patch(datePresetRange(preset.id))}
            className={clsx(
              'rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
              activePreset === preset.id
                ? 'bg-brand-50 text-brand-700 ring-1 ring-brand-200'
                : 'bg-slate-50 text-slate-600 hover:bg-slate-100',
            )}
          >
            {preset.label}
          </button>
        ))}
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <Input
          label="Başlangıç Tarihi"
          type="date"
          value={filters.dateFrom}
          onChange={(e) => patch({ dateFrom: e.target.value })}
        />
        <Input
          label="Bitiş Tarihi"
          type="date"
          value={filters.dateTo}
          onChange={(e) => patch({ dateTo: e.target.value })}
        />
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-slate-700">Teslimat Tipi</span>
          <select
            value={filters.orderType}
            onChange={(e) => patch({ orderType: e.target.value as OrderFilters['orderType'] })}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          >
            <option value="all">Tümü</option>
            <option value="delivery">Teslimat</option>
            <option value="pickup">Gel-Al</option>
          </select>
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-slate-700">Sıralama</span>
          <select
            value={filters.sortBy}
            onChange={(e) => patch({ sortBy: e.target.value as OrderSortKey })}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          >
            <option value="date-desc">Tarih (yeni → eski)</option>
            <option value="date-asc">Tarih (eski → yeni)</option>
            <option value="total-desc">Tutar (yüksek → düşük)</option>
            <option value="total-asc">Tutar (düşük → yüksek)</option>
          </select>
        </label>
        <Input
          label="Min. Tutar (₺)"
          type="number"
          min="0"
          step="0.01"
          value={filters.minTotal}
          onChange={(e) => patch({ minTotal: e.target.value })}
          placeholder="0"
        />
        <Input
          label="Max. Tutar (₺)"
          type="number"
          min="0"
          step="0.01"
          value={filters.maxTotal}
          onChange={(e) => patch({ maxTotal: e.target.value })}
          placeholder="Sınırsız"
        />
      </div>

      {filtersActive && (
        <div className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
          <Filter className="h-3.5 w-3.5" />
          Aktif filtreler uygulanıyor — {filteredCount} sonuç
        </div>
      )}
    </div>
  );
}
